import type { Observation } from "@medplum/fhirtypes";

import { LOINCCodes } from "@/fhir/enums/loinc";
import { Systems } from "@/fhir/enums/systems";
import { findCodingInCodeableConcept, getCodeableConceptCode } from "@/utils/medplum";
import { pickObservationNewerByVersionIdThenRecencyThenId } from "@/utils/observation-recency";

function observationHasLaborStageLoinc(observation: Observation): boolean {
  return observation.code?.coding?.some((coding) => coding.code === LOINCCodes.LaborStage) ?? false;
}

/** LOINC labor-stage answer code from any coding slot (order is not guaranteed on Observation). */
export function getLaborStageAnswerCode(observation: Observation): string | undefined {
  const codings = observation.valueCodeableConcept?.coding ?? [];
  const fromLoinc = codings.find((c) => c.system === Systems.LOINC && c.code)?.code;
  if (fromLoinc) return fromLoinc;
  return codings.find((c) => c.code)?.code;
}

/**
 * Returns deduplicated final labor-stage observations keyed by stage code/timestamp and newest version.
 * @param observations Observation collection to deduplicate.
 * @returns Final observations with older duplicates removed.
 */
export function dedupeFinalLaborStageObservations(observations: Observation[]): Observation[] {
  const dedupedByKey = observations
    .filter((observation) => observation.status === "final")
    .reduce(
      (acc, observation) => {
        const isLaborStage = observationHasLaborStageLoinc(observation);
        const primaryCode =
          findCodingInCodeableConcept(observation.code, { code: LOINCCodes.LaborStage })?.code ??
          (getCodeableConceptCode(observation.code) || undefined);
        const stageAnswerCode = isLaborStage ? getLaborStageAnswerCode(observation) : undefined;
        const key = isLaborStage
          ? `${LOINCCodes.LaborStage}-${stageAnswerCode ?? "unknown"}`
          : `${primaryCode ?? "unknown"}-${observation.effectiveDateTime}`;
        if (
          !acc[key] ||
          pickObservationNewerByVersionIdThenRecencyThenId(observation, acc[key]) === observation
        ) {
          acc[key] = observation;
        }
        return acc;
      },
      {} as Record<string, Observation>,
    );

  return Object.values(dedupedByKey);
}
